/* eslint-disable import/named */
import Vue from 'vue';
import {
  apiReadAllYoutubeVideos,
  apiReadAllVideoCategories,
  apiMarkVideoAsWatched,
  apiToggleFavourite,
  apiReadAllFavourites,
  apiReadSingularVideo,
  apiHandleVideoMetric,
  apiGetDashboardWebinars,
  apiRegisterForWebinar,
  apiCheckWebinarRegistration,
} from '@/api/videos';

export default {
  state: () => ({
    videos: [],
    loadingVideos: false,
    tags: [
      'categories',
      'most recent',
      'older',
      'most views',
      'favourites',
    ],
    filteredCategories: [],
    sortingData: {
      sortBy: {
        categories: ['date_published'],
        'most recent': ['date_published'],
        older: ['date_published'],
        'most views': ['view_count'],
      },
      sortDesc: {
        categories: [true],
        'most recent': [true],
        older: [false],
        'most views': [true],
      },
    },
    selectedTag: 'categories',
    loadingTags: false,
    currentVideo: null,
    currentPage: 1,
    totalVideos: null,
    itemsPerPage: 12,
    videoPageScrollTop: null,
    showPlayer: false,
    favouritedVideos: [],

    // ── Webinar Banner ─────────────────────────────────────────────────────
    /** Banner webinars sorted by priority (LIVE → SCHEDULED → ENDED) */
    bannerWebinars: [],
    bannerLoading: false,
    /** ID of the webinar whose join modal is currently open, or null */
    activeWebinarId: null,
    /** ID of the webinar minimized to the floating panel, or null.
     *  Cleared automatically when the webinar is no longer LIVE.
     *  Drops on F5 (Vuex is in-memory). */
    minimizedWebinarId: null,

    // ── Webinar Registration ──────────────────────────────────────────────
    /** Map of webinar_id → boolean for registration status */
    webinarRegistrations: {},
  }),

  getters: {
    getVideoTags(state) {
      return (video) => video.categories
        .filter((x) => state.filteredCategories
          .some((y) => y.toLowerCase() === x.toLowerCase()));
    },

    sortBy(state) {
      return state.sortingData.sortBy[state.selectedTag];
    },

    sortDesc(state) {
      return state.sortingData.sortDesc[state.selectedTag];
    },

    categories(state) {
      return state.selectedTag === 'categories' ? state.filteredCategories : [];
    },

    // ── Webinar Banner getters ───────────────────────────────────────────
    activeWebinar: (state) => state.bannerWebinars.find((w) => w.id === state.activeWebinarId) || null,
    minimizedWebinar: (state) => state.bannerWebinars.find((w) => w.id === state.minimizedWebinarId) || null,
    isModalOpen: (state) => state.activeWebinarId !== null,
    isMinimized: (state) => state.minimizedWebinarId !== null,

    // ── Webinar Registration getters ─────────────────────────────────────
    isRegisteredForWebinar: (state) => (webinarId) => !!state.webinarRegistrations[webinarId],
  },

  mutations: {
    setLoadingVideos(state, payload) {
      state.loadingVideos = payload;
    },

    setVideos(state, payload) {
      state.videos = payload;
    },

    addVideos(state, payload) {
      state.videos = state.videos.concat(payload);
    },

    setTags(state, payload) {
      state.tags = payload;
    },

    setSelectedTag(state, payload) {
      state.selectedTag = payload;
    },

    setLoadingTags(state, payload) {
      state.loadingTags = payload;
    },

    setCurrentVideo(state, payload) {
      state.currentVideo = payload;
    },

    setCurrentPage(state, payload) {
      state.currentPage = payload;
    },

    setTotal(state, payload) {
      state.totalVideos = payload;
    },

    setVideoPageScrollTop(state, payload) {
      state.videoPageScrollTop = payload;
    },

    setShowPlayer(state, payload) {
      state.showPlayer = payload;
    },

    setVideoAsWatched(state, payload) {
      const video = state.videos.find((x) => x.id === payload);
      video.is_watched = true;
    },

    toggleFavouriteVideo(state, payload) {
      const video = state.videos.find((x) => x.id === payload);
      video.is_favourited = !video.is_favourited;
    },

    setFavouritedVideos(state, payload) {
      state.favouritedVideos = payload;
    },

    setVideoCategories(state, payload) {
      state.filteredCategories = payload;
    },

    // ── Webinar Banner mutations ─────────────────────────────────────────
    setBannerWebinars(state, webinars) { state.bannerWebinars = webinars; },
    setBannerLoading(state, val) { state.bannerLoading = val; },
    setActiveWebinarId(state, id) { state.activeWebinarId = id; },
    setMinimizedWebinarId(state, id) { state.minimizedWebinarId = id; },

    // ── Webinar Registration mutations ───────────────────────────────────
    setWebinarRegistration(state, { webinarId, isRegistered }) {
      Vue.set(state.webinarRegistrations, webinarId, isRegistered);
    },
  },

  actions: {
    async loadAllVideos({ commit, state }) {
      const { currentPage, results, total } = await apiReadAllYoutubeVideos({
        itemsPerPage: state.itemsPerPage,
        page: state.currentPage,
      });
      commit('setCurrentPage', currentPage);
      commit('setTotal', total);
      if (results && state.currentPage === 1) commit('setVideos', results);
      if (results && state.currentPage !== 1) commit('addVideos', results);
    },

    async markVideoAsWatched({ commit, state }, currentVideoId) {
      await apiMarkVideoAsWatched({ id: currentVideoId });
      commit('setCurrentVideo', { ...state.currentVideo, is_watched: true });
      commit('setVideoAsWatched', currentVideoId);
    },

    async toggleFavouriteVideos({ commit, state }, currentVideoId) {
      await apiToggleFavourite({ id: currentVideoId });
      commit('setCurrentVideo', { ...state.currentVideo, is_favourited: !state.currentVideo.is_favourited });
      commit('toggleFavouriteVideo', currentVideoId);
    },

    // eslint-disable-next-line no-unused-vars
    async handleVideoMetric(_, options) {
      await apiHandleVideoMetric(options);
    },

    async loadAllFavouriteVideos({ commit }) {
      const result = await apiReadAllFavourites();
      if (result) commit('setFavouritedVideos', result);
    },

    async loadSingularVideoAsCurrent({ commit }, payload) {
      const result = await apiReadSingularVideo(payload);
      if (result) commit('setCurrentVideo', result);
    },

    async loadAllVideoCategories({ commit }, payload = false) {
      try {
        const { results } = await apiReadAllVideoCategories({ include_untracked: payload });
        commit('setVideoCategories', results);
      } catch (e) {
        // ignore the resultant error
      }
    },

    // ── Webinar Banner actions ───────────────────────────────────────────

    /**
     * Fetch banner-eligible webinars (LIVE, SCHEDULED, recently ENDED).
     * Auto-clears the minimized panel if the minimized webinar is no longer LIVE.
     */
    async fetchBannerWebinars({ commit, state }) {
      commit('setBannerLoading', true);
      try {
        const data = await apiGetDashboardWebinars();
        const webinars = data?.results ?? [];
        commit('setBannerWebinars', webinars);
        // Populate registration status from backend response
        webinars.forEach((w) => {
          if (w.is_registered !== undefined) {
            commit('setWebinarRegistration', { webinarId: w.id, isRegistered: w.is_registered });
          }
        });
        if (state.minimizedWebinarId !== null) {
          const minimized = webinars.find((w) => w.id === state.minimizedWebinarId);
          if (!minimized || minimized.status !== 'LIVE') {
            commit('setMinimizedWebinarId', null);
          }
        }
      } catch {
        // Banner is non-critical; absorb fetch errors so the dashboard renders normally.
      } finally {
        commit('setBannerLoading', false);
      }
    },

    /** Open the join modal; clears minimized state (user returning from panel). */
    openModal({ commit }, webinarId) {
      commit('setMinimizedWebinarId', null);
      commit('setActiveWebinarId', webinarId);
    },

    /** Close the join modal without minimizing. */
    closeModal({ commit }) {
      commit('setActiveWebinarId', null);
    },

    /** Minimize the modal to the floating panel. */
    minimize({ commit }, webinarId) {
      commit('setActiveWebinarId', null);
      commit('setMinimizedWebinarId', webinarId);
    },

    /** Destroy the minimized panel (e.g. webinar ended). */
    clearMinimized({ commit }) {
      commit('setMinimizedWebinarId', null);
    },

    // ── Webinar Registration actions ─────────────────────────────────────

    async checkWebinarRegistration({ commit }, webinarId) {
      const { is_registered } = await apiCheckWebinarRegistration({ webinar_id: webinarId });
      commit('setWebinarRegistration', { webinarId, isRegistered: is_registered });
    },

    async registerForWebinar({ commit }, payload) {
      const result = await apiRegisterForWebinar(payload);
      commit('setWebinarRegistration', { webinarId: payload.webinar_id, isRegistered: true });
      return result;
    },
  },

  namespaced: true,
};
